import { CurrencyPipe, DatePipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { TodayTimeOrDatePipe } from '@zero/shared/ui-pipes';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { TradingTimesService } from '@zero/trade/domain';
import { Subscription, debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { BezugsrechtBackLinkComponent } from '../back-link/bezugsrecht-back-link.component';
import { BezugsrechtBase } from '../bezugsrecht.base';

@Component({
  imports: [
    TranslocoPipe,
    BezugsrechtBackLinkComponent,
    ReactiveFormsModule,
    TodayTimeOrDatePipe,
    DecimalPipe,
    CurrencyPipe,
    DatePipe,
    UpperCasePipe,
    ButtonComponent,
  ],
  templateUrl: './bezugsrecht-buy-custom.component.html',
})
export class BezugsrechtBuyCustomComponent extends BezugsrechtBase implements OnInit {
  private readonly _buyOffsetInMinutes = -30;
  private readonly _typingDebounceTime = 500;

  public orderForm: UntypedFormGroup;

  public get shareAmount(): AbstractControl {
    return this.orderForm?.get('shareAmount');
  }

  private readonly _tradingTimesService = inject(TradingTimesService);

  public override ngOnInit(): void {
    super.ngOnInit();

    this._tradingTimesService.initHolidays();
    this.initForm();

    if (this.model?.directivePreview) {
      this.shareAmount.setValue(this.model.directivePreview.bezugAktien);
    }

    this.valueChangesDebouncedBackendCall();

    this.service.bezugsrechtData$
      .pipe(
        filter((data) => !!data?.directivePreview),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.orderForm.setErrors(this.validateActiveTradingHours(this.orderForm));
        this.shareAmount.setErrors(this.validateShareAmount(this.shareAmount));
      });
  }

  public override back = (): void => {
    this.router.navigateByUrl(this.model.buyableAmount <= 0 ? this.rootUrl : `${this.rootUrl}/${this.BEZUGSRECHT_ROUTES.BUY}`);
  };

  public override loadPreview(): void {
    this.msgService.clearAllMessages();

    if (!this.orderForm.valid) {
      this.msgService.addErrorMessage(this.translateService.translate('bezugsrecht-service.error'));
      return;
    }

    super.loadPreview(this.shareAmount.value);
  }

  public forward(): void {
    if (this.model?.directivePreview) {
      this.router.navigateByUrl(`${this.rootUrl}/${this.BEZUGSRECHT_ROUTES.SUMMARY}`);
    }
  }

  private initForm(): void {
    this.orderForm = new UntypedFormGroup(
      {
        shareAmount: new UntypedFormControl('', [
          Validators.required,
          Validators.min(1),
          Validators.pattern('[0-9]*'),
          this.validateShareAmount.bind(this),
        ]),
      },
      [this.validateActiveTradingHours.bind(this)],
    );
  }

  private validateShareAmount(control: AbstractControl): ValidationErrors | null {
    let returnValue: ValidationErrors | null = null;

    if (!control?.value) {
      return null;
    }

    if (control.value.includes(',')) {
      const value = Number(control.value.replace(',', '.'));
      returnValue = Number.isNaN(value) ? { invalidValue: true } : { isFragment: true };
    } else if (typeof control.value !== 'string' || Number.isNaN(Number(control.value)) || Number(control.value) < 1) {
      returnValue = { invalidValue: true };
    } else if (
      this.model?.announcement?.bezugVerhaeltnisZaehler &&
      !Number.isInteger(Number(control.value) / this.model.announcement.bezugVerhaeltnisZaehler)
    ) {
      returnValue = { valueNotMultipleOfDenominator: true };
    } else if (this.model?.directivePreview?.bezugBzrWertSumme && this.model.buyingPower < this.model.directivePreview.bezugBzrWertSumme) {
      returnValue = {
        bpTooLow: true,
      };
    }

    if (returnValue) {
      control.setErrors(returnValue);
    }

    return returnValue;
  }

  private validateActiveTradingHours(control: AbstractControl): ValidationErrors | null {
    if (!this.model?.asset || !this.model?.announcement) {
      return null;
    }

    let returnValue: ValidationErrors | null = null;
    if (
      control?.value &&
      this.model?.directivePreview?.bezugBzr &&
      this.model?.asset &&
      this.model?.announcement.bzrHandelbarBroker &&
      !this._tradingTimesService.isActiveTradingHoursTradingEndOffset(null, this.model?.asset, this._buyOffsetInMinutes)
    ) {
      console.log('validateActiveTradingHours');

      returnValue = {
        noActiveTradingHours: true,
      };
      control.setErrors(returnValue);
    }

    return returnValue;
  }

  private valueChangesDebouncedBackendCall(): Subscription {
    return this.orderForm.valueChanges
      .pipe(
        debounceTime(this._typingDebounceTime),
        distinctUntilChanged((previus, current) => previus.shareAmount === current.shareAmount),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ shareAmount }) => {
        this.msgService.clearAllMessages();

        super.loadPreview(shareAmount);
      });
  }
}
